import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, Skeleton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Pagination, IconButton,
  Tooltip, Avatar, Tabs, Tab, Drawer, Divider, CircularProgress,
  Alert, MenuItem, Select, InputLabel, FormControl, InputAdornment,
  LinearProgress, Badge, Stack, Grid
} from "@mui/material";
import { getData, postData, putData } from "../../Webservice/Webservice";
import SELLER_PAYOUT_ROUTES from "../../API_Route/SellerPayoutRoute";
import {
  MdHistory, MdAccountBalance, MdPayment, MdRefresh,
  MdCheckCircle, MdPending, MdError, MdEdit, MdOutlineStorefront,
  MdTrendingUp, MdOutlinePayments, MdOutlineAccountBalanceWallet
} from "react-icons/md";
import { FiFilter as FilterIcon } from "react-icons/fi";
import { HiOutlineDocumentText } from "react-icons/hi2";

const PAGE_SIZE = 20;

const StatusBadge = ({ status }) => {
  const map = {
    completed: { label: "Completed", bg: "rgba(16,185,129,0.12)", color: "#059669", icon: <MdCheckCircle size={13} /> },
    processing: { label: "Processing", bg: "rgba(59,130,246,0.12)", color: "#2563eb", icon: <MdPending size={13} /> },
    initiated: { label: "Initiated", bg: "rgba(251,191,36,0.12)", color: "#d97706", icon: <MdPending size={13} /> },
    failed: { label: "Failed", bg: "rgba(239,68,68,0.12)", color: "#dc2626", icon: <MdError size={13} /> },
    pending: { label: "Pending", bg: "rgba(251,191,36,0.12)", color: "#d97706", icon: <MdPending size={13} /> },
  };
  const s = map[status] || map.pending;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "4px", px: 1.2, py: 0.4,
      borderRadius: "20px", bgcolor: s.bg, color: s.color, fontSize: "0.72rem", fontWeight: 700,
      fontFamily: "Mulish,sans-serif", whiteSpace: "nowrap"
    }}>
      {s.icon} {s.label}
    </Box>
  );
};

const SummaryCard = ({ icon, label, value, color, sub }) => (
  <Box sx={{
    bgcolor: "#fff", borderRadius: 2, p: 2.5, border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", flex: 1
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ bgcolor: color + "18", p: 1.2, borderRadius: 1.5 }}>
        {React.cloneElement(icon, { size: 22, color })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", fontFamily: "Mulish,sans-serif" }}>{label}</Typography>
        <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, fontFamily: "Mulish,sans-serif", color: "#111827" }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", fontFamily: "Mulish,sans-serif" }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

function SellerPayout() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState(0);

  // Payout dialog
  const [payoutDialog, setPayoutDialog] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [payoutMode, setPayoutMode] = useState("manual"); // "manual" | "cashfree"
  const [payoutForm, setPayoutForm] = useState({ paymentMethod: "bank_transfer", referenceNumber: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [payoutResult, setPayoutResult] = useState(null);

  // Bank details dialog
  const [bankDialog, setBankDialog] = useState(false);
  const [bankForm, setBankForm] = useState({ accountHolder: "", accountNumber: "", ifsc: "", bankName: "", upiId: "" });
  const [bankSaving, setBankSaving] = useState(false);

  // History dialog
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(null);

  // Ledger dialog
  const [ledgerDialog, setLedgerDialog] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Filter
  const [filters, setFilters] = useState({ sellerName: "" });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({ sellerName: "" });

  const token = `Bearer ${sessionStorage.getItem("token") || ""}`;

  const totalPages = Math.max(1, Math.ceil(sellers.length / PAGE_SIZE));
  const paginatedSellers = sellers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fmt = (v) => {
    const n = Number(v);
    if (!v && v !== 0) return "—";
    if (isNaN(n)) return "—";
    return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
      const res = await getData(SELLER_PAYOUT_ROUTES.GET_SELLER_PAYOUT, params, token);
      setSellers(res?.sellers || res?.vendors || []);
    } catch (e) {
      setSellers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchSellers(); }, [fetchSellers]);

  // Summary stats
  const stats = {
    totalPending: sellers.reduce((s, x) => s + (x.pendingBalance > 0 ? x.pendingBalance : 0), 0),
    totalSales: sellers.reduce((s, x) => s + (x.totalSaleAmount || 0), 0),
    totalPaid: sellers.reduce((s, x) => s + (x.totalPaidToSeller || 0), 0),
    sellersWithPending: sellers.filter(x => x.pendingBalance > 0).length,
  };

  // Filter by tab: 0=All, 1=Pending, 2=No Pending
  const filteredSellers = tab === 1
    ? paginatedSellers.filter(s => s.pendingBalance > 0)
    : tab === 2
      ? paginatedSellers.filter(s => !(s.pendingBalance > 0))
      : paginatedSellers;

  // ── Open Payout Dialog ────────────────────────────────────────────────────
  const openPayoutDialog = (seller) => {
    setSelectedSeller(seller);
    setPayoutMode(seller.bankDetails?.accountNumber ? "cashfree" : "manual");
    setPayoutForm({ paymentMethod: "bank_transfer", referenceNumber: "", remarks: "" });
    setPayoutResult(null);
    setPayoutDialog(true);
  };

  const handleInitiatePayout = async () => {
    if (!selectedSeller) return;
    setSubmitting(true);
    setPayoutResult(null);
    try {
      const body = {
        sellerId: selectedSeller.sellerId,
        paymentMethod: payoutForm.paymentMethod,
        referenceNumber: payoutForm.referenceNumber,
        remarks: payoutForm.remarks,
        useCashfree: payoutMode === "cashfree"
      };
      const res = await postData(SELLER_PAYOUT_ROUTES.UPDATE_SELLER_PAYOUT, body, token);
      setPayoutResult({ type: "success", message: res?.message || "Payout initiated successfully!" });
      fetchSellers();
    } catch (err) {
      setPayoutResult({ type: "error", message: err?.response?.data?.message || "Payout failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bank Details Dialog ───────────────────────────────────────────────────
  const openBankDialog = (seller) => {
    setSelectedSeller(seller);
    setBankForm({
      accountHolder: seller.bankDetails?.accountHolder || "",
      accountNumber: seller.bankDetails?.accountNumber || "",
      ifsc: seller.bankDetails?.ifsc || "",
      bankName: seller.bankDetails?.bankName || "",
      upiId: seller.bankDetails?.upiId || ""
    });
    setBankDialog(true);
  };

  const handleSaveBankDetails = async () => {
    setBankSaving(true);
    try {
      await putData(`${SELLER_PAYOUT_ROUTES.UPDATE_BANK_DETAILS}/${selectedSeller.sellerId}`, bankForm, token);
      setBankDialog(false);
      fetchSellers();
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to save bank details");
    } finally {
      setBankSaving(false);
    }
  };

  // ── History Dialog ────────────────────────────────────────────────────────
  const openHistoryDialog = async (seller) => {
    setSelectedSeller(seller);
    setHistoryDialog(true);
    setHistoryLoading(true);
    try {
      const res = await getData(`${SELLER_PAYOUT_ROUTES.GET_SELLER_PAYOUT_HISTORY}/${seller.sellerId}`, undefined, token);
      setHistoryData(res);
    } catch { setHistoryData(null); }
    finally { setHistoryLoading(false); }
  };

  const handleCheckStatus = async (payoutId) => {
    setCheckingStatus(payoutId);
    try {
      const res = await getData(`${SELLER_PAYOUT_ROUTES.CHECK_PAYOUT_STATUS}/${payoutId}`, undefined, token);
      const updated = res?.payout;
      if (updated && historyData?.payouts) {
        setHistoryData(prev => ({
          ...prev,
          payouts: prev.payouts.map(p => p._id === payoutId ? { ...p, ...updated } : p)
        }));
      }
    } catch { }
    finally { setCheckingStatus(null); }
  };

  // ── Ledger Dialog ─────────────────────────────────────────────────────────
  const openLedgerDialog = async (seller) => {
    setSelectedSeller(seller);
    setLedgerDialog(true);
    setLedgerLoading(true);
    try {
      const res = await getData(`${SELLER_PAYOUT_ROUTES.GET_SELLER_LEDGER}/${seller.sellerId}?status=pending`, undefined, token);
      setLedgerData(res?.ledgers || []);
    } catch { setLedgerData([]); }
    finally { setLedgerLoading(false); }
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fb", minHeight: "100vh", p: { xs: 1, md: 2 } }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 800, color: "#111827" }}>
            Seller Payouts
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", fontFamily: "Mulish,sans-serif" }}>
            Manage and process seller payouts
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<FilterIcon />}
            onClick={() => { setTempFilters(filters); setFilterDrawerOpen(true); }}
            sx={{
              fontFamily: "Mulish,sans-serif", borderRadius: "8px", textTransform: "none",
              fontWeight: 600, borderColor: "#e5e7eb", color: "#374151",
              "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" }
            }}>
            Filters
          </Button>
          <Button variant="outlined" startIcon={<MdRefresh size={16} />}
            onClick={fetchSellers}
            sx={{
              fontFamily: "Mulish,sans-serif", borderRadius: "8px", textTransform: "none",
              fontWeight: 600, borderColor: "#e5e7eb", color: "#374151",
              "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" }
            }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <SummaryCard icon={<MdOutlineAccountBalanceWallet />} color="#f59e0b"
          label="Total Pending Payout" value={fmt(stats.totalPending)}
          sub={`${stats.sellersWithPending} seller(s) awaiting`} />
        <SummaryCard icon={<MdTrendingUp />} color="#10b981"
          label="Total Gross Sales" value={fmt(stats.totalSales)} />
        <SummaryCard icon={<MdOutlinePayments />} color="#3b82f6"
          label="Total Paid So Far" value={fmt(stats.totalPaid)} />
        <SummaryCard icon={<MdOutlineStorefront />} color="#8b5cf6"
          label="Active Sellers" value={sellers.length} sub="on platform" />
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: "#fff", borderRadius: 2, border: "1px solid #e5e7eb",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden"
      }}>
        <Box sx={{
          borderBottom: "1px solid #e5e7eb", px: 2, display: "flex",
          justifyContent: "space-between", alignItems: "center"
        }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              "& .MuiTab-root": {
                fontFamily: "Mulish,sans-serif", fontWeight: 700,
                textTransform: "none", fontSize: "0.85rem"
              },
              "& .Mui-selected": { color: "#f59e0b !important" },
              "& .MuiTabs-indicator": { bgcolor: "#f59e0b" }
            }}>
            <Tab label={`All Sellers (${sellers.length})`} />
            <Tab label={`Pending Payout (${stats.sellersWithPending})`} />
            <Tab label="No Pending" />
          </Tabs>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{
                "& th": {
                  bgcolor: "#fafafa", fontFamily: "Mulish,sans-serif",
                  fontWeight: 800, fontSize: "0.72rem", textTransform: "uppercase",
                  letterSpacing: "0.05em", color: "#6b7280", borderBottom: "2px solid #e5e7eb",
                  py: 1.5, whiteSpace: "nowrap"
                }
              }}>
                <TableCell>Seller</TableCell>
                <TableCell>Total Sales</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Payable</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Pending Balance</TableCell>
                <TableCell>Pending Orders</TableCell>
                <TableCell>Last Payout</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <TableCell key={j}><Skeleton height={28} /></TableCell>
                    ))}
                  </TableRow>
                ))
                : filteredSellers.length > 0
                  ? filteredSellers.map((seller, idx) => {
                    const hasPending = seller.pendingBalance > 0;
                    const hasBankDetails = seller.bankDetails?.accountNumber;
                    return (
                      <TableRow key={seller.sellerId || idx} hover
                        sx={{ "&:hover": { bgcolor: "#fffbeb" }, transition: "background 0.15s" }}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{
                              width: 34, height: 34, bgcolor: "#ffd102", color: "#000",
                              fontFamily: "Mulish,sans-serif", fontWeight: 800, fontSize: "0.85rem"
                            }}>
                              {(seller.sellerName || "S")[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{
                                fontWeight: 700, fontFamily: "Mulish,sans-serif",
                                fontSize: "0.88rem", color: "#111827"
                              }}>
                                {seller.sellerName || "N/A"}
                              </Typography>
                              <Typography sx={{
                                fontSize: "0.72rem", color: "#9ca3af",
                                fontFamily: "Mulish,sans-serif"
                              }}>
                                {seller.business_name || seller.email || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{
                            fontFamily: "Mulish,sans-serif", fontWeight: 600,
                            color: "#374151", fontSize: "0.88rem"
                          }}>
                            {fmt(seller.totalSaleAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{
                            fontFamily: "Mulish,sans-serif", fontWeight: 600,
                            color: "#ef4444", fontSize: "0.88rem"
                          }}>
                            {fmt(seller.adminCommissionAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{
                            fontFamily: "Mulish,sans-serif", fontWeight: 700,
                            color: "#374151", fontSize: "0.88rem"
                          }}>
                            {fmt(seller.payableToSeller)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{
                            fontFamily: "Mulish,sans-serif", fontWeight: 700,
                            color: "#059669", fontSize: "0.88rem"
                          }}>
                            {fmt(seller.totalPaidToSeller)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography sx={{
                              fontFamily: "Mulish,sans-serif", fontWeight: 800,
                              color: hasPending ? "#d97706" : "#6b7280", fontSize: "0.95rem"
                            }}>
                              {fmt(seller.pendingBalance > 0 ? seller.pendingBalance : 0)}
                            </Typography>
                            {hasPending && (
                              <LinearProgress variant="determinate"
                                value={Math.min(100, (seller.pendingBalance / seller.payableToSeller) * 100)}
                                sx={{
                                  mt: 0.5, height: 3, borderRadius: 2,
                                  bgcolor: "#fef3c7", "& .MuiLinearProgress-bar": { bgcolor: "#f59e0b" }
                                }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {seller.pendingLedgerCount > 0
                            ? <Chip label={`${seller.pendingLedgerCount} orders`} size="small"
                              onClick={() => openLedgerDialog(seller)}
                              sx={{
                                fontFamily: "Mulish,sans-serif", fontWeight: 700, cursor: "pointer",
                                bgcolor: "#fef3c7", color: "#92400e", fontSize: "0.72rem",
                                "&:hover": { bgcolor: "#fde68a" }
                              }} />
                            : <Typography sx={{
                              color: "#9ca3af", fontSize: "0.8rem",
                              fontFamily: "Mulish,sans-serif"
                            }}>—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          <Box>
                            {seller.lastPayoutDate
                              ? <>
                                <Typography sx={{
                                  fontFamily: "Mulish,sans-serif", fontSize: "0.8rem",
                                  color: "#374151"
                                }}>
                                  {new Date(seller.lastPayoutDate).toLocaleDateString("en-IN")}
                                </Typography>
                                <StatusBadge status={seller.lastPayoutStatus || "completed"} />
                              </>
                              : <Typography sx={{
                                color: "#9ca3af", fontSize: "0.8rem",
                                fontFamily: "Mulish,sans-serif"
                              }}>Never</Typography>
                            }
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={hasBankDetails
                            ? `${seller.bankDetails.accountHolder || ""} • ${seller.bankDetails.bankName || ""}`
                            : "No bank details"}>
                            <Box sx={{
                              display: "flex", alignItems: "center", gap: 0.5,
                              cursor: "pointer"
                            }} onClick={() => openBankDialog(seller)}>
                              <MdAccountBalance size={16}
                                color={hasBankDetails ? "#10b981" : "#d1d5db"} />
                              <Typography sx={{
                                fontSize: "0.75rem", fontFamily: "Mulish,sans-serif",
                                color: hasBankDetails ? "#10b981" : "#9ca3af"
                              }}>
                                {hasBankDetails ? "Linked" : "Add"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                            <Tooltip title="Initiate Payout">
                              <span>
                                <Button size="small" variant="contained"
                                  disabled={!hasPending}
                                  onClick={() => openPayoutDialog(seller)}
                                  sx={{
                                    fontFamily: "Mulish,sans-serif", textTransform: "none",
                                    fontWeight: 700, bgcolor: "#ffd102", color: "#000",
                                    fontSize: "0.75rem", px: 1.5, boxShadow: "none",
                                    "&:hover": { bgcolor: "#f59e0b", boxShadow: "none" },
                                    "&.Mui-disabled": { bgcolor: "#f3f4f6", color: "#9ca3af" }
                                  }}>
                                  Pay
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="View History">
                              <IconButton size="small" onClick={() => openHistoryDialog(seller)}
                                sx={{ color: "#6b7280", "&:hover": { color: "#374151", bgcolor: "#f3f4f6" } }}>
                                <MdHistory size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Bank Details">
                              <IconButton size="small" onClick={() => openBankDialog(seller)}
                                sx={{ color: "#6b7280", "&:hover": { color: "#374151", bgcolor: "#f3f4f6" } }}>
                                <MdEdit size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  : (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                        <Typography sx={{ fontFamily: "Mulish,sans-serif", color: "#9ca3af", fontSize: "0.95rem" }}>
                          No seller payout data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
              }
            </TableBody>
          </Table>
        </TableContainer>

        {sellers.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: "Mulish,sans-serif",
                  "&.Mui-selected": { bgcolor: "#ffd102", color: "#000" }
                }
              }}
              shape="rounded" showFirstButton showLastButton />
          </Box>
        )}
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          PAYOUT DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={payoutDialog} onClose={() => !submitting && setPayoutDialog(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 16px 48px rgba(0,0,0,0.12)" } }}>
        <DialogTitle sx={{
          fontFamily: "Mulish,sans-serif", fontWeight: 800, fontSize: "1.1rem",
          borderBottom: "1px solid #e5e7eb", pb: 2
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <MdOutlinePayments size={22} color="#f59e0b" />
            Initiate Payout — {selectedSeller?.sellerName}
          </Box>
        </DialogTitle>

        {payoutResult && (
          <Alert severity={payoutResult.type} sx={{ mx: 3, mt: 2, fontFamily: "Mulish,sans-serif" }}>
            {payoutResult.message}
          </Alert>
        )}

        <DialogContent sx={{ pt: 2 }}>
          {/* Pending summary */}
          <Box sx={{
            bgcolor: "#fefce8", border: "1px solid #fde68a", borderRadius: 2,
            p: 2.5, mb: 3
          }}>
            <Typography sx={{ fontSize: "0.78rem", color: "#92400e", fontFamily: "Mulish,sans-serif", mb: 0.5 }}>
              PENDING PAYOUT AMOUNT
            </Typography>
            <Typography sx={{
              fontSize: "2rem", fontWeight: 900, fontFamily: "Mulish,sans-serif",
              color: "#78350f", lineHeight: 1
            }}>
              {fmt(selectedSeller?.pendingBalance)}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#a16207", fontFamily: "Mulish,sans-serif", mt: 0.5 }}>
              {selectedSeller?.pendingLedgerCount || 0} pending order(s) will be settled
            </Typography>
          </Box>

          {/* Payout Mode Toggle */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
            {[
              {
                key: "cashfree", label: "Via Payment Gateway", icon: <MdPayment size={16} />,
                desc: "Automatic bank transfer via Cashfree", disabled: !selectedSeller?.bankDetails?.accountNumber
              },
              {
                key: "manual", label: "Manual Entry", icon: <MdAccountBalance size={16} />,
                desc: "Record payout with reference number", disabled: false
              }
            ].map(m => (
              <Box key={m.key} onClick={() => !m.disabled && setPayoutMode(m.key)}
                sx={{
                  flex: 1, border: `2px solid ${payoutMode === m.key ? "#f59e0b" : "#e5e7eb"}`,
                  borderRadius: 2, p: 2, cursor: m.disabled ? "not-allowed" : "pointer",
                  bgcolor: payoutMode === m.key ? "#fffbeb" : "#fff",
                  opacity: m.disabled ? 0.5 : 1, transition: "all 0.15s",
                  "&:hover": m.disabled ? {} : { borderColor: "#fbbf24" }
                }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1, mb: 0.5,
                  color: payoutMode === m.key ? "#d97706" : "#6b7280"
                }}>
                  {m.icon}
                  <Typography sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 700, fontSize: "0.82rem" }}>
                    {m.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.72rem", color: "#9ca3af" }}>
                  {m.disabled ? "No bank details — add bank details first" : m.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Cashfree Mode: Show bank details */}
          {payoutMode === "cashfree" && selectedSeller?.bankDetails && (
            <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, p: 2, mb: 2 }}>
              <Typography sx={{
                fontFamily: "Mulish,sans-serif", fontWeight: 700,
                fontSize: "0.78rem", color: "#166534", mb: 1
              }}>
                TRANSFER TO
              </Typography>
              <Grid container spacing={1}>
                {[
                  ["Account Holder", selectedSeller.bankDetails.accountHolder],
                  ["Account Number", selectedSeller.bankDetails.accountNumber],
                  ["IFSC Code", selectedSeller.bankDetails.ifsc],
                  ["Bank Name", selectedSeller.bankDetails.bankName],
                  ["UPI ID", selectedSeller.bankDetails.upiId],
                ].map(([k, v]) => v ? (
                  <Grid item xs={6} key={k}>
                    <Typography sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.7rem", color: "#6b7280" }}>{k}</Typography>
                    <Typography sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem", fontWeight: 700, color: "#111827" }}>{v}</Typography>
                  </Grid>
                ) : null)}
              </Grid>
            </Box>
          )}

          {/* Manual Mode fields */}
          {payoutMode === "manual" && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontFamily: "Mulish,sans-serif" }}>Payment Method</InputLabel>
                <Select value={payoutForm.paymentMethod} label="Payment Method"
                  onChange={e => setPayoutForm(p => ({ ...p, paymentMethod: e.target.value }))}
                  sx={{ fontFamily: "Mulish,sans-serif" }}>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth size="small" label="Reference / Transaction Number"
                value={payoutForm.referenceNumber}
                onChange={e => setPayoutForm(p => ({ ...p, referenceNumber: e.target.value }))}
                sx={{ mb: 2, "& .MuiInputBase-root": { fontFamily: "Mulish,sans-serif" } }} />
            </>
          )}

          <TextField fullWidth size="small" label="Remarks (optional)" multiline rows={2}
            value={payoutForm.remarks}
            onChange={e => setPayoutForm(p => ({ ...p, remarks: e.target.value }))}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish,sans-serif" } }} />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPayoutDialog(false)} disabled={submitting}
            sx={{ fontFamily: "Mulish,sans-serif", textTransform: "none", color: "#6b7280" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleInitiatePayout}
            disabled={submitting || !!payoutResult?.type === "success"}
            startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : <MdPayment size={17} />}
            sx={{
              fontFamily: "Mulish,sans-serif", textTransform: "none", fontWeight: 800,
              bgcolor: "#ffd102", color: "#000", px: 3, boxShadow: "none",
              "&:hover": { bgcolor: "#f59e0b", boxShadow: "none" }
            }}>
            {submitting ? "Processing..." : payoutMode === "cashfree" ? "Initiate via Gateway" : "Record Payout"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          BANK DETAILS DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={bankDialog} onClose={() => setBankDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 800, borderBottom: "1px solid #e5e7eb" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MdAccountBalance size={20} color="#3b82f6" />
            Bank Details — {selectedSeller?.sellerName}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {[
            { key: "accountHolder", label: "Account Holder Name" },
            { key: "accountNumber", label: "Account Number" },
            { key: "ifsc", label: "IFSC Code" },
            { key: "bankName", label: "Bank Name" },
            { key: "upiId", label: "UPI ID (optional)" },
          ].map(f => (
            <TextField key={f.key} fullWidth size="small" label={f.label}
              value={bankForm[f.key]}
              onChange={e => setBankForm(p => ({ ...p, [f.key]: e.target.value }))}
              sx={{
                mb: 2, "& .MuiInputBase-root": { fontFamily: "Mulish,sans-serif" },
                "& .MuiInputLabel-root": { fontFamily: "Mulish,sans-serif" }
              }} />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setBankDialog(false)}
            sx={{ fontFamily: "Mulish,sans-serif", textTransform: "none", color: "#6b7280" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveBankDetails} disabled={bankSaving}
            sx={{
              fontFamily: "Mulish,sans-serif", textTransform: "none", fontWeight: 700,
              bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }
            }}>
            {bankSaving ? "Saving..." : "Save Bank Details"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          HISTORY DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 800, borderBottom: "1px solid #e5e7eb" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MdHistory size={20} color="#8b5cf6" />
            Payout History — {selectedSeller?.sellerName}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {historyLoading
            ? <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
            : !historyData?.payouts?.length
              ? <Typography sx={{
                py: 4, textAlign: "center", color: "#9ca3af",
                fontFamily: "Mulish,sans-serif"
              }}>No payout history found</Typography>
              : (
                <TableContainer sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        "& th": {
                          fontFamily: "Mulish,sans-serif", fontWeight: 700,
                          fontSize: "0.72rem", textTransform: "uppercase", color: "#6b7280",
                          bgcolor: "#fafafa", borderBottom: "2px solid #e5e7eb"
                        }
                      }}>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Gateway</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyData.payouts.map(p => (
                        <TableRow key={p._id} hover>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem" }}>
                            {new Date(p.createdAt).toLocaleDateString("en-IN")}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 700, fontSize: "0.88rem" }}>
                            {fmt(p.amount)}
                          </TableCell>
                          <TableCell sx={{
                            fontFamily: "Mulish,sans-serif", fontSize: "0.82rem",
                            textTransform: "capitalize"
                          }}>
                            {(p.paymentMethod || "—").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem", color: "#6b7280" }}>
                            {p.referenceNumber || p.gatewayTransferId || "—"}
                          </TableCell>
                          <TableCell>
                            {p.payoutGateway
                              ? <Chip label={p.payoutGateway} size="small"
                                sx={{
                                  fontSize: "0.68rem", fontFamily: "Mulish,sans-serif",
                                  bgcolor: "#eff6ff", color: "#1d4ed8"
                                }} />
                              : <Typography sx={{
                                color: "#9ca3af", fontSize: "0.8rem",
                                fontFamily: "Mulish,sans-serif"
                              }}>Manual</Typography>
                            }
                          </TableCell>
                          <TableCell><StatusBadge status={p.status} /></TableCell>
                          <TableCell>
                            {p.payoutGateway === "cashfree" && p.status === "processing" && (
                              <Button size="small" onClick={() => handleCheckStatus(p._id)}
                                disabled={checkingStatus === p._id}
                                startIcon={checkingStatus === p._id
                                  ? <CircularProgress size={12} /> : <MdRefresh size={14} />}
                                sx={{
                                  fontFamily: "Mulish,sans-serif", textTransform: "none",
                                  fontSize: "0.72rem", color: "#3b82f6"
                                }}>
                                Check
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
          }
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistoryDialog(false)}
            sx={{ fontFamily: "Mulish,sans-serif", textTransform: "none", color: "#6b7280" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          PENDING LEDGER DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={ledgerDialog} onClose={() => setLedgerDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 800, borderBottom: "1px solid #e5e7eb" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HiOutlineDocumentText size={20} color="#f59e0b" />
            Pending Orders — {selectedSeller?.sellerName}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {ledgerLoading
            ? <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
            : !ledgerData?.length
              ? <Typography sx={{
                py: 4, textAlign: "center", color: "#9ca3af",
                fontFamily: "Mulish,sans-serif"
              }}>No pending orders</Typography>
              : (
                <TableContainer sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        "& th": {
                          fontFamily: "Mulish,sans-serif", fontWeight: 700,
                          fontSize: "0.72rem", textTransform: "uppercase", color: "#6b7280",
                          bgcolor: "#fafafa", borderBottom: "2px solid #e5e7eb"
                        }
                      }}>
                        <TableCell>Order</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Gross</TableCell>
                        <TableCell>Commission</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledgerData.map(l => (
                        <TableRow key={l._id} hover>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem", fontWeight: 600 }}>
                            {l.orderId?.orderNumber || "—"}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem" }}>
                            {l.orderId?.createdAt ? new Date(l.orderId.createdAt).toLocaleDateString("en-IN") : "—"}
                          </TableCell>
                          <TableCell sx={{
                            fontFamily: "Mulish,sans-serif", fontSize: "0.82rem",
                            textTransform: "capitalize"
                          }}>
                            {l.type}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem" }}>
                            {fmt(l.grossAmount)}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish,sans-serif", fontSize: "0.82rem", color: "#ef4444" }}>
                            {fmt(l.commissionAmount)} ({l.commissionPercentage}%)
                          </TableCell>
                          <TableCell sx={{
                            fontFamily: "Mulish,sans-serif", fontWeight: 700, fontSize: "0.88rem",
                            color: "#059669"
                          }}>
                            {fmt(l.netAmount)}
                          </TableCell>
                          <TableCell><StatusBadge status={l.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
          }
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLedgerDialog(false)}
            sx={{ fontFamily: "Mulish,sans-serif", textTransform: "none", color: "#6b7280" }}>
            Close
          </Button>
          {ledgerData?.length > 0 && (
            <Button variant="contained" onClick={() => { setLedgerDialog(false); openPayoutDialog(selectedSeller); }}
              sx={{
                fontFamily: "Mulish,sans-serif", textTransform: "none", fontWeight: 700,
                bgcolor: "#ffd102", color: "#000", "&:hover": { bgcolor: "#f59e0b" }
              }}>
              Proceed to Pay {fmt(selectedSeller?.pendingBalance)}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Filter Drawer ───────────────────────────────────────────────── */}
      <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
        <Box sx={{ width: 320, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: "Mulish,sans-serif", fontWeight: 800 }}>
            Filter Sellers
          </Typography>
          <TextField fullWidth size="small" label="Seller Name"
            value={tempFilters.sellerName}
            onChange={e => setTempFilters(p => ({ ...p, sellerName: e.target.value }))}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish,sans-serif" } }} />
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button fullWidth variant="outlined"
              onClick={() => { setTempFilters({ sellerName: "" }); setFilters({ sellerName: "" }); setFilterDrawerOpen(false); }}
              sx={{
                fontFamily: "Mulish,sans-serif", textTransform: "none", fontWeight: 600,
                borderRadius: "8px", borderColor: "#e5e7eb", color: "#374151"
              }}>
              Clear
            </Button>
            <Button fullWidth variant="contained"
              onClick={() => { setFilters(tempFilters); setFilterDrawerOpen(false); }}
              sx={{
                fontFamily: "Mulish,sans-serif", textTransform: "none", fontWeight: 700,
                borderRadius: "8px", bgcolor: "#ffd102", color: "#000",
                "&:hover": { bgcolor: "#f59e0b" }, boxShadow: "none"
              }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default SellerPayout;
